// eslint-disable-next-line no-restricted-imports
import api from 'app/gamma/src/api';
import { client } from '@trello/graphql';
import type {
  NotificationsCountQuery,
  NotificationsCountQueryVariables,
} from './NotificationsCountQuery.generated';
import { NotificationsCountDocument } from './NotificationsCountQuery.generated';
// eslint-disable-next-line no-restricted-imports
import { normalizeNotifications } from 'app/gamma/src/api/normalizers/notification';
import { isFilteringByUnreadNotifications } from 'app/src/components/NotificationsMenu/notificationsMenuState';
// eslint-disable-next-line no-restricted-imports
import type {
  NotificationGroupResponse,
  NotificationGroupsResponse,
  NotificationResponse,
} from 'app/gamma/src/types/responses';
// eslint-disable-next-line no-restricted-imports
import type {
  NotificationGroupModel,
  NotificationModel,
} from 'app/gamma/src/types/models';
// eslint-disable-next-line no-restricted-imports
import type { StandardThunkAction, Dispatch } from 'app/gamma/src/types';
// eslint-disable-next-line no-restricted-imports
import type { Action } from '@trello/redux';
// eslint-disable-next-line no-restricted-imports
import { actionCreator } from '@trello/redux';
import { groupNotifications } from 'app/src/components/NotificationsMenu/groupNotifications';
import { refreshIfMissing } from 'app/src/components/ActionEntities/customActions';
import type {
  UnreadNotificationsQuery,
  UnreadNotificationsQueryVariables,
} from './UnreadNotificationsQuery.generated';
import { UnreadNotificationsDocument } from './UnreadNotificationsQuery.generated';
import { NotificationGroupsDocument } from './NotificationGroupsQuery.generated';
import type {
  UnreadNotificationGroupsQuery,
  UnreadNotificationGroupsQueryVariables,
} from './UnreadNotificationGroupsQuery.generated';
import { UnreadNotificationGroupsDocument } from './UnreadNotificationGroupsQuery.generated';
import { featureFlagClient } from '@trello/feature-flag-client';
import { notificationsPaneState } from 'app/src/components/NotificationsMenu/notificationsPaneState';
import { notificationsState } from 'app/src/components/NotificationsMenu/notificationsState';
import { loadNotificationGroupsSuccess } from 'app/src/components/NotificationsMenu/loadNotificationGroupsSuccess';
import { loadUnreadNotificationsSuccess } from 'app/src/components/NotificationsMenu/loadUnreadNotificationsSuccess';
import { loadReactionsFromNotifications } from 'app/src/components/Reactions/loadReactionsFromNotifications';

// Action types
export const LOAD_NOTIFICATION_GROUPS_SUCCESS = Symbol(
  'loaders/LOAD_NOTIFICATION_GROUPS_SUCCESS',
);
export const LOAD_UNREAD_NOTIFICATIONS_SUCCESS = Symbol(
  'loaders/LOAD_UNREAD_NOTIFICATIONS_SUCCESS',
);

interface LoadNotificationGroupsParams {
  limit?: number;
  skipNotificationGroups?: NotificationGroupModel[];
}

export type LoadNotificationGroupsSuccessAction = Action<
  typeof LOAD_NOTIFICATION_GROUPS_SUCCESS,
  {
    notificationGroupsResponse: NotificationGroupResponse[];
    isFilteringByUnread: boolean;
  }
>;

export type LoadUnreadNotificationsSuccessAction = Action<
  typeof LOAD_UNREAD_NOTIFICATIONS_SUCCESS,
  {
    notificationGroupsResponse: NotificationGroupResponse[];
    partialUnreadNotificationGroups: NotificationGroupModel[];
    partialUnreadNotifications: NotificationModel[];
    isFilteringByUnread: boolean;
  }
>;

const loadNotificationGroupsSuccessAction = actionCreator<LoadNotificationGroupsSuccessAction>(
  LOAD_NOTIFICATION_GROUPS_SUCCESS,
);

const loadUnreadNotificationsSuccessAction = actionCreator<LoadUnreadNotificationsSuccessAction>(
  LOAD_UNREAD_NOTIFICATIONS_SUCCESS,
);

export const loadUnreadNotificationsCount = async () => {
  try {
    const { data, error } = await client.query<
      NotificationsCountQuery,
      NotificationsCountQueryVariables
    >({
      query: NotificationsCountDocument,
      context: {
        operationName: 'NotificationsCount',
        document: NotificationsCountDocument,
      },
    });

    if (error || !data.notificationsCount) {
      return;
    }

    const count = JSON.parse(data.notificationsCount);

    notificationsState.setValue({
      ...notificationsState.value,
      unreadCount: count,
    });
  } catch (err) {
    // no-op
  }
};

const loadSomeNotificationGroups = ({
  limit = 10,
  skipNotificationGroups = [],
}: LoadNotificationGroupsParams = {}): StandardThunkAction => {
  return async (dispatch: Dispatch) => {
    try {
      const skip = skipNotificationGroups.length;

      notificationsPaneState.setValue({
        loading: limit,
      });

      const useGraphqlForNotificationsLoader = featureFlagClient.get(
        'fep.use-graphql-for-notifications-loader',
        false,
      );

      let notificationGroupsResponse: NotificationGroupsResponse;

      if (useGraphqlForNotificationsLoader) {
        const response = await client.query({
          query: NotificationGroupsDocument,
          variables: { limit, skip },
          // eslint-disable-next-line @trello/disallow-fetch-policies
          fetchPolicy: 'network-only',
          context: {
            operationName: 'NotificationGroups',
            document: NotificationGroupsDocument,
          },
        });

        notificationGroupsResponse = response.data.notificationGroups;
      } else {
        notificationGroupsResponse = await api.client.rest.get<NotificationGroupsResponse>(
          'members/me/notificationGroups',
          {
            query: {
              limit,
              skip,
              card_fields: [
                'id',
                'name',
                'start',
                'subscribed',
                'dateLastActivity',
                'desc',
                'descData',
                'dueReminder',
                'email',
                'idAttachmentCover',
                'idChecklists',
                'idLabels',
                'idMembers',
                'idMembersVoted',
                'idShort',
                'isTemplate',
                'cardRole',
                'due',
                'dueComplete',
                'idBoard',
                'idList',
                'closed',
                'manualCoverAttachment',
                'pos',
                'shortLink',
                'shortUrl',
                'url',
                'checkItemStates',
                'badges',
                'cover',
              ],
              card_board_fields: ['id', 'name', 'prefs', 'url', 'subscribed'],
            },
            headers: {
              'X-Trello-Operation-Name': 'loadSomeNotificationGroups',
            },
          },
        );
      }

      // Check if the groups contain any unknown translations, and kick off a
      // load for those if needed
      const translationKeys: Set<string> = notificationGroupsResponse.reduce(
        (keys: Set<string>, group) => {
          group.notifications.forEach((notification) =>
            keys.add(notification.display.translationKey),
          );

          return keys;
        },
        new Set<string>(),
      );

      await refreshIfMissing(...translationKeys);

      const isFilteringByUnread = isFilteringByUnreadNotifications();

      notificationsPaneState.setValue({
        loading: 0,
        moreToLoad:
          notificationsPaneState.value.loading -
            notificationGroupsResponse.length ===
          0,
      });
      if (notificationsPaneState.value.cancelLoading) {
        notificationsPaneState.setValue({
          cancelLoading: false,
        });
      } else {
        dispatch(
          loadNotificationGroupsSuccessAction({
            notificationGroupsResponse,
            isFilteringByUnread,
          }),
        );
        loadNotificationGroupsSuccess({
          notificationGroupsResponse,
          isFilteringByUnread,
        });
        loadReactionsFromNotifications(notificationGroupsResponse);
      }
    } catch (err) {
      notificationsPaneState.setValue({
        loading: 0,
      });
    }
  };
};

/**
 * This returns the total number of unread notifications in a list
 * of NotificationGroups.
 *
 * This is distinct from the response to `/1/members/me/notificationsCount`,
 * which will include unread notifications that haven't been lazily-loaded
 * yet.
 */
const getLoadedUnreadNotificationCount = (
  notificationGroups: NotificationGroupModel[],
) =>
  notificationGroups.reduce(
    (unreadCount: number, notificationGroup: NotificationGroupModel) =>
      unreadCount +
      notificationGroup.notifications.reduce(
        (unreadCountInner: number, notification: NotificationModel) =>
          unreadCountInner + (notification.unread ? 1 : 0),
        0,
      ),
    0,
  );

export const loadUnreadNotificationGroups = ({
  limit = 10,
  skipNotificationGroups = [],
}: LoadNotificationGroupsParams = {}): StandardThunkAction => {
  return async (dispatch: Dispatch) => {
    try {
      // Since we're using `members/me/notifications` instead of
      // `members/me/notificationGroups`, we need to adjust set the skip
      // parameter to match the number of notifications, rather than the number
      // of notificationGroups.
      const skip = getLoadedUnreadNotificationCount(skipNotificationGroups);

      notificationsPaneState.setValue({
        loadingUnread: limit,
      });

      /*
       Let's load all the unread notifications and group them.

       This api only provides partial groups of notifications
       the groups will only have the unread notifications and none
       of the read ones, this is no good for the ui.

       Therefore the id's for the groups with cards need to be collected
       and then we can request the full groups from the api and merge them
       with the partial groups.
      */

      const page = Math.floor(skip / limit);

      const useGraphqlForNotificationsLoader = featureFlagClient.get(
        'fep.use-graphql-for-notifications-loader',
        false,
      );

      let partialUnreadNotifications: NotificationModel[];

      if (useGraphqlForNotificationsLoader) {
        const { data } = await client.query<
          UnreadNotificationsQuery,
          UnreadNotificationsQueryVariables
        >({
          query: UnreadNotificationsDocument,
          variables: { limit, page, readFilter: 'unread' },
          // eslint-disable-next-line @trello/disallow-fetch-policies
          fetchPolicy: 'network-only',
          context: {
            operationName: 'UnreadNotifications',
            document: UnreadNotificationsDocument,
          },
        });

        const notifications = data.notifications;

        if (!notifications) {
          return;
        }

        partialUnreadNotifications = normalizeNotifications(
          notifications as NotificationResponse[],
        );
      } else {
        const unreadNotificationsResponse = await api.client.rest.get<
          NotificationResponse[]
        >('members/me/notifications', {
          query: {
            limit,
            page,
            read_filter: 'unread',
            display: 'true',
          },
          headers: {
            'X-Trello-Operation-Name': 'loadUnreadNotificationGroups:partial',
          },
        });

        partialUnreadNotifications = normalizeNotifications(
          unreadNotificationsResponse,
        );
      }

      const partialUnreadNotificationGroups = groupNotifications(
        partialUnreadNotifications,
      );

      const cardIdsToLoad = partialUnreadNotificationGroups.map(
        (group) => group.id,
      );

      let notificationGroupsResponse: NotificationGroupsResponse = [];

      if (cardIdsToLoad.length) {
        if (useGraphqlForNotificationsLoader) {
          const { data } = await client.query<
            UnreadNotificationGroupsQuery,
            UnreadNotificationGroupsQueryVariables
          >({
            query: UnreadNotificationGroupsDocument,
            variables: { idCards: cardIdsToLoad },
            // eslint-disable-next-line @trello/disallow-fetch-policies
            fetchPolicy: 'network-only',
            context: {
              operationName: 'UnreadNotificationGroups',
              document: UnreadNotificationGroupsDocument,
            },
          });

          const notificationGroups = data.notificationGroups;

          if (!notificationGroups) {
            return;
          }

          notificationGroupsResponse = notificationGroups as NotificationGroupsResponse;
        } else {
          const unreadNotificationGroupsResponse = await api.client.rest.get<NotificationGroupsResponse>(
            'notificationGroups',
            {
              query: { idCards: cardIdsToLoad.join(',') },
              headers: {
                'X-Trello-Operation-Name': 'loadUnreadNotificationGroups:full',
              },
            },
          );

          notificationGroupsResponse = unreadNotificationGroupsResponse;
        }
      }

      const isFilteringByUnread = isFilteringByUnreadNotifications();

      const moreUnreadToLoad =
        notificationsPaneState.value.loadingUnread -
          partialUnreadNotifications.length ===
        0;

      notificationsPaneState.setValue({
        loadingUnread: 0,
        moreUnreadToLoad,
      });
      if (notificationsPaneState.value.cancelLoadingUnread) {
        notificationsPaneState.setValue({
          cancelLoadingUnread: false,
        });
      } else {
        dispatch(
          loadUnreadNotificationsSuccessAction({
            notificationGroupsResponse,
            partialUnreadNotificationGroups,
            partialUnreadNotifications,
            isFilteringByUnread,
          }),
        );
        loadUnreadNotificationsSuccess({
          notificationGroupsResponse,
          partialUnreadNotificationGroups,
          partialUnreadNotifications,
          isFilteringByUnread,
        });
        loadReactionsFromNotifications(notificationGroupsResponse);
      }
    } catch (err) {
      notificationsPaneState.setValue({
        loadingUnread: 0,
      });
    }
  };
};

export const loadNotificationGroups = ({
  limit = 10,
  skipNotificationGroups = [],
}: LoadNotificationGroupsParams = {}): StandardThunkAction => {
  return async (dispatch: Dispatch) => {
    const isFilteringByUnread = isFilteringByUnreadNotifications();
    const { loadingUnread, loading: loadingAll } = notificationsPaneState.value;

    // cancel pending request if we need to
    if (loadingUnread || loadingAll) {
      if (isFilteringByUnread && loadingAll) {
        notificationsPaneState.setValue({
          cancelLoadingUnread: true,
        });
      } else if (!isFilteringByUnread && loadingUnread) {
        notificationsPaneState.setValue({
          cancelLoading: true,
        });
      } else {
        // since we're already loading from this group, don't load more
        return;
      }
    }
    if (isFilteringByUnread) {
      dispatch(
        loadUnreadNotificationGroups({
          limit,
          skipNotificationGroups,
        }),
      );
    } else {
      dispatch(
        loadSomeNotificationGroups({
          limit,
          skipNotificationGroups,
        }),
      );
    }
  };
};
